import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

/* ─── Global Font ─── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lato:wght@300;400;700;900&display=swap');

  :root {
    --russian-red:  #CD0302;
    --sunburst:     #F0C400;
    --edam:         #F5D19D;
    --citrusy:      #F88702;
    --green:        #4F7E17;
    --green-light:  #6aad1f;
    --edam-light:   #fdf3e3;
    --edam-dark:    #e8c070;
    --soil:         #3d2b1f;
    --cream:        #fffdf5;
    --fog:          #f7f2ea;
  }
`;

/* ─── Animations ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1);    box-shadow: 0 0 0 6px rgba(79,126,23,0.18); }
  50%       { transform: scale(1.06); box-shadow: 0 0 0 10px rgba(79,126,23,0.10); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

/* ─── Page ─── */
const PageWrapper = styled.div`
  font-family: 'Lato', sans-serif;
  min-height: 100vh;
  background:
    radial-gradient(ellipse at 0% 0%,    rgba(79,126,23,0.10) 0%, transparent 50%),
    radial-gradient(ellipse at 100% 100%, rgba(240,196,0,0.08) 0%, transparent 50%),
    #fffdf5;
  padding: 36px 16px 72px;
  color: #3d2b1f;
`;

const Container = styled.div`
  max-width: 760px;
  margin: 0 auto;
  animation: ${fadeUp} 0.55s ease-out;
`;

/* ─── Top Banner ─── */
const TopBanner = styled.div`
  background: linear-gradient(135deg, var(--green) 0%, #3a6012 100%);
  border-radius: 14px 14px 0 0;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: -2px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.85);
  text-transform: uppercase;

  span { color: #F0C400; }
`;

/* ─── Success Card ─── */
const SuccessCard = styled.div`
  background: #fff;
  border: 1.5px solid var(--edam);
  border-radius: 0 0 18px 18px;
  padding: 40px 36px 36px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 4px 24px rgba(79,126,23,0.10);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--green), var(--citrusy), var(--sunburst));
  }

  @media (max-width: 600px) { padding: 28px 20px 24px; }
`;

const CheckCircle = styled.div`
  width: 76px;
  height: 76px;
  background: linear-gradient(135deg, var(--green), var(--green-light));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 22px;
  animation: ${pulse} 2.8s ease-in-out infinite;

  svg { width: 38px; height: 38px; fill: #fff; }
`;

const SuccessTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.4rem, 4vw, 2rem);
  font-weight: 700;
  font-style: italic;
  color: var(--soil);
  margin: 0 0 12px;
  line-height: 1.25;
`;

const SuccessTitle2 = styled.span`
  color: var(--green);
`;

const SuccessMsg = styled.p`
  color: #7a6a58;
  font-size: 0.95rem;
  line-height: 1.8;
  margin: 0;
  max-width: 520px;
  margin-inline: auto;
`;

/* ─── Section Card ─── */
const SectionCard = styled.div`
  background: #fff;
  border: 1.5px solid var(--edam);
  border-radius: 18px;
  margin-bottom: 20px;
  box-shadow: 0 2px 16px rgba(79,126,23,0.07);
  overflow: hidden;
  animation: ${fadeUp} 0.6s ease-out ${props => props.$delay || '0s'} both;
`;

const SectionHeader = styled.div`
  background: linear-gradient(90deg, rgba(79,126,23,0.07), rgba(240,196,0,0.06));
  border-bottom: 1px dashed var(--edam-dark);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1rem;
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    margin: 0;
  }

  .icon {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
`;

const SectionBody = styled.div`
  padding: 0;
`;

/* ─── Order Table ─── */
const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const OrderRow = styled.tr`
  border-bottom: 1px solid #f7f2ea;
  transition: background 0.15s;

  &:last-child { border-bottom: none; }
  &:hover { background: rgba(245,209,157,0.12); }

  td {
    padding: 12px 20px;
    color: #7a6a58;
    vertical-align: top;
    line-height: 1.5;

    &:first-child {
      width: 40%;
      color: var(--soil);
      font-weight: 700;
      font-size: 0.85rem;
      letter-spacing: 0.02em;
    }
    &:last-child { color: var(--soil); }
  }

  @media (max-width: 600px) {
    display: block;
    padding: 8px 0;
    border-bottom: 1px solid #f7f2ea;

    td {
      display: block;
      padding: 4px 16px;
      width: 100% !important;
      &:first-child {
        font-size: 0.75rem;
        color: #9a8878;
        padding-bottom: 2px;
      }
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  background: rgba(79,126,23,0.10);
  color: var(--green);
  border: 1px solid rgba(79,126,23,0.25);
  padding: 3px 12px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

/* ─── Blood Section ─── */
const BloodSectionCard = styled(SectionCard)`
  border-top: 4px solid var(--russian-red);
`;

const BloodSectionHeader = styled(SectionHeader)`
  background: linear-gradient(90deg, rgba(205,3,2,0.06), rgba(240,196,0,0.05));
  border-bottom-color: rgba(205,3,2,0.15);
`;

const DonorBadge = styled.div`
  background: rgba(248,135,2,0.08);
  border: 1.5px solid rgba(248,135,2,0.25);
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--citrusy);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
`;

const BloodDescText = styled.p`
  font-size: 0.9rem;
  color: #7a6a58;
  line-height: 1.8;
  margin: 0 0 20px;
`;

const BloodGroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 16px;
`;

const BloodGroupButton = styled.button`
  background: ${props => props.selected
    ? 'linear-gradient(135deg, var(--russian-red), #a80201)'
    : '#fff'};
  color: ${props => props.selected ? '#fff' : 'var(--russian-red)'};
  border: 2px solid ${props => props.selected ? 'var(--russian-red)' : 'rgba(205,3,2,0.35)'};
  padding: 14px 8px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: ${props => props.selected ? '0 4px 14px rgba(205,3,2,0.3)' : '0 1px 4px rgba(0,0,0,0.04)'};

  &:hover:not(:disabled) {
    background: ${props => props.selected
    ? 'linear-gradient(135deg, #a80201, #860201)'
    : 'rgba(205,3,2,0.06)'};
    transform: translateY(-2px);
    box-shadow: 0 5px 16px rgba(205,3,2,0.22);
  }

  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const ThankYouCard = styled.div`
  background: rgba(205,3,2,0.04);
  border: 1.5px solid rgba(205,3,2,0.18);
  border-radius: 14px;
  padding: 20px;
  margin-top: 4px;
  animation: ${fadeUp} 0.4s ease-out;
`;

const ThankYouCardTitle = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 1.05rem;
  font-weight: 700;
  font-style: italic;
  color: var(--russian-red);
  margin-bottom: 10px;
`;

const ThankYouCardText = styled.p`
  font-size: 0.88rem;
  color: #7a6a58;
  line-height: 1.8;
  margin: 0 0 8px;
`;

const SaveStatus = styled.div`
  font-size: 0.82rem;
  color: var(--green);
  font-style: italic;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;

  &::before {
    content: '';
    width: 14px; height: 14px;
    border: 2px solid rgba(79,126,23,0.25);
    border-top-color: var(--green);
    border-radius: 50%;
    display: inline-block;
    animation: ${spin} 0.8s linear infinite;
    flex-shrink: 0;
  }
`;

/* ─── Home Button ─── */
const HomeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, var(--green) 0%, var(--green-light) 100%);
  color: #fff;
  border: none;
  padding: 14px 40px;
  border-radius: 14px;
  cursor: pointer;
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  font-weight: 700;
  font-style: italic;
  transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
  margin: 0 auto;
  box-shadow: 0 4px 18px rgba(79,126,23,0.32);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 26px rgba(79,126,23,0.4);
    filter: brightness(1.05);
  }
`;

const OrganicTrustStrip = styled.div`
  text-align: center;
  font-size: 0.75rem;
  color: #b0a090;
  margin-top: 24px;
  letter-spacing: 0.06em;
`;

/* ═══════════════════════════════════════════ */

function ThankYouPage() {
  const location = useLocation();
  const navigate  = useNavigate();
  const orderSummary = location.state?.orderSummary;

  const [selectedBloodGroup, setSelectedBloodGroup] = useState('');
  const [isSavingBlood, setIsSavingBlood]           = useState(false);
  const [totalDonors, setTotalDonors]               = useState(5000);

  /* ── unchanged logic ── */
  useEffect(() => {
    if (!orderSummary) { navigate('/', { replace: true }); return; }

    let storedOrders = [];
    try {
      const storedData = localStorage.getItem('orderHistory');
      if (storedData) storedOrders = JSON.parse(storedData);
    } catch (error) {
      console.error("localStorage থেকে অর্ডারের ইতিহাস পার্স করতে ত্রুটি:", error);
      storedOrders = [];
    }

    if (Object.keys(orderSummary).length > 0) {
      const simplifiedOrder = {
        orderId: orderSummary.orderId,
        user_id: orderSummary.user_id,
        customerInfo: orderSummary.customerInfo,
        cartItems: orderSummary.cartItems,
        totalAmount: orderSummary.totalAmount,
        paymentMethod: orderSummary.paymentMethod,
        paymentDetails: orderSummary.paymentDetails,
        orderDate: orderSummary.orderDate,
        orderTime: orderSummary.orderTime,
        created_at: new Date().toISOString(),
        CustomerDeliveryCharge: orderSummary.CustomerDeliveryCharge,
      };
      storedOrders.push(simplifiedOrder);
    }

    const twentyDaysAgo = new Date();
    twentyDaysAgo.setDate(twentyDaysAgo.getDate() - 20);
    const updatedOrders = storedOrders.filter(order => new Date(order.created_at) >= twentyDaysAgo);

    try {
      localStorage.setItem('orderHistory', JSON.stringify(updatedOrders));
    } catch (error) {
      console.error("localStorage এ অর্ডারের ইতিহাস সেভ করতে ত্রুটি:", error);
    }
  }, [orderSummary, navigate]);

  useEffect(() => {
    fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/get_blood_donor_count')
      .then(res => res.json())
      .then(data => { if (data.total_donors) setTotalDonors(data.total_donors); })
      .catch(err => console.error('Error fetching donor count:', err));
  }, []);

  const handleBloodGroupClick = async (bloodGroup) => {
    setSelectedBloodGroup(bloodGroup);
    setIsSavingBlood(true);
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/save_blood_group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: orderSummary.customerInfo.phone,
          name: orderSummary.customerInfo.name,
          bloodGroup,
          location: orderSummary.customerInfo.address,
        }),
      });
      if (!response.ok) {
        alert('তথ্য সংরক্ষণে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
        setSelectedBloodGroup('');
      }
    } catch (error) {
      console.error('Blood donation save error:', error);
      alert('তথ্য সংরক্ষণে সমস্যা হয়েছে।');
      setSelectedBloodGroup('');
    } finally {
      setIsSavingBlood(false);
    }
  };

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

  if (!orderSummary) return null;

  const paymentLabel =
    orderSummary.paymentMethod === 'cashOnDelivery' ? 'ক্যাশ অন ডেলিভারি' :
    orderSummary.paymentMethod === 'bkash'          ? 'বিকাশ' : 'নগদ';

  return (
    <>
      <GlobalStyle />
      <PageWrapper>
        <Container>

          {/* ── Top Banner ── */}
          <TopBanner>
            <span>✓</span> অর্ডার সফলভাবে গৃহীত হয়েছে
          </TopBanner>

          {/* ── Success Card ── */}
          <SuccessCard>
            <CheckCircle>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z" />
              </svg>
            </CheckCircle>

            <SuccessTitle>
              🎉 অভিনন্দন! আপনার অর্ডার <SuccessTitle2>সফল</SuccessTitle2> হয়েছে
            </SuccessTitle>
            <SuccessMsg>
              আপনার অর্ডারটি সফলভাবে গৃহীত হয়েছে। আমরা খুব শীঘ্রই আপনার সাথে যোগাযোগ করব এবং পণ্য ডেলিভারি করব।
            </SuccessMsg>
          </SuccessCard>

          {/* ── Order Details ── */}
          <SectionCard $delay="0.15s">
            <SectionHeader>
              <span className="icon">📋</span>
              <h2>অর্ডারের বিস্তারিত</h2>
            </SectionHeader>
            <SectionBody>
              <OrderTable>
                <tbody>
                  <OrderRow>
                    <td>📅 অর্ডার তারিখ</td>
                    <td>{orderSummary.orderDate}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>🕐 অর্ডার সময়</td>
                    <td>{orderSummary.orderTime}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>💰 মোট মূল্য</td>
                    <td><strong style={{ color: 'var(--russian-red)', fontFamily: "'Playfair Display', serif", fontSize: '1.05rem' }}>৳{orderSummary.totalAmount + orderSummary.CustomerDeliveryCharge}</strong></td>
                  </OrderRow>
                  <OrderRow>
                    <td>💳 পেমেন্ট পদ্ধতি</td>
                    <td><StatusBadge>{paymentLabel}</StatusBadge></td>
                  </OrderRow>
                  {orderSummary.paymentDetails && (
                    <>
                      <OrderRow>
                        <td>🔖 TrxID</td>
                        <td>{orderSummary.paymentDetails.trxId}</td>
                      </OrderRow>
                      <OrderRow>
                        <td>📱 পেমেন্ট নম্বর</td>
                        <td>{orderSummary.paymentDetails.phoneNo}</td>
                      </OrderRow>
                    </>
                  )}
                  <OrderRow>
                    <td>👤 গ্রাহকের নাম</td>
                    <td>{orderSummary.customerInfo.name}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>📞 মোবাইল নম্বর</td>
                    <td>{orderSummary.customerInfo.phone}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>📍 ঠিকানা</td>
                    <td>{orderSummary.customerInfo.address}</td>
                  </OrderRow>
                  {orderSummary.customerInfo.notes && (
                    <OrderRow>
                      <td>📝 বিশেষ নোট</td>
                      <td>{orderSummary.customerInfo.notes}</td>
                    </OrderRow>
                  )}
                </tbody>
              </OrderTable>
            </SectionBody>
          </SectionCard>

          {/* ── Blood Donation ── */}
          <BloodSectionCard $delay="0.3s">
            <BloodSectionHeader>
              <span className="icon">🩸</span>
              <h2>রক্তদান করুন, জীবন বাঁচান</h2>
            </BloodSectionHeader>
            <SectionBody style={{ padding: '20px' }}>

              <DonorBadge>
                🤝 আমাদের সাথে যুক্ত আছেন {(totalDonors + 5000).toLocaleString()} পরিবার
              </DonorBadge>

              {!selectedBloodGroup ? (
                <>
                  <BloodDescText>
                    আপনার রক্তের গ্রুপ শেয়ার করুন এবং জরুরি মুহূর্তে আপনার এলাকার মা, বাবা, ভাই, বোন বা প্রিয়জনকে সাহায্য করুন।
                    একটি ছোট্ট পদক্ষেপে আপনি হতে পারেন কারো জীবন রক্ষাকারী।
                    আপনার দেওয়া তথ্য সম্পূর্ণ গোপনীয় থাকবে এবং শুধুমাত্র জরুরি প্রয়োজনে ব্যবহার করা হবে।
                  </BloodDescText>
                  <BloodGroupGrid>
                    {bloodGroups.map(group => (
                      <BloodGroupButton
                        key={group}
                        onClick={() => handleBloodGroupClick(group)}
                        disabled={isSavingBlood}
                      >
                        {group}
                      </BloodGroupButton>
                    ))}
                  </BloodGroupGrid>
                  {isSavingBlood && <SaveStatus>সংরক্ষণ হচ্ছে...</SaveStatus>}
                </>
              ) : (
                <ThankYouCard>
                  <ThankYouCardTitle>🙏 অসংখ্য ধন্যবাদ!</ThankYouCardTitle>
                  <ThankYouCardText>
                    আপনার রক্তের গ্রুপ <strong style={{ color: 'var(--russian-red)' }}>{selectedBloodGroup}</strong> সফলভাবে সংরক্ষিত হয়েছে।
                    আপনার এই মহৎ উদ্যোগ জরুরি মুহূর্তে কারো জীবন বাঁচাতে পারে।
                    মানবতার সেবায় আপনার এই অবদান সত্যিই প্রশংসনীয়।
                    প্রয়োজনে আমরা আপনার সাথে যোগাযোগ করব।
                  </ThankYouCardText>
                  <ThankYouCardText style={{ fontSize: '0.8rem', marginTop: '12px', color: '#9a8878' }}>
                    পরিবর্তন করতে চান? নিচে আপনার নতুন গ্রুপ সিলেক্ট করুন:
                  </ThankYouCardText>
                  <BloodGroupGrid style={{ marginTop: '14px' }}>
                    {bloodGroups.map(group => (
                      <BloodGroupButton
                        key={group}
                        selected={group === selectedBloodGroup}
                        onClick={() => handleBloodGroupClick(group)}
                        disabled={isSavingBlood}
                      >
                        {group}
                      </BloodGroupButton>
                    ))}
                  </BloodGroupGrid>
                  {isSavingBlood && <SaveStatus>আপডেট হচ্ছে...</SaveStatus>}
                </ThankYouCard>
              )}
            </SectionBody>
          </BloodSectionCard>

          {/* ── Home Button ── */}
          <HomeButton onClick={() => navigate('/')}>
            🏠 হোম পেজে ফিরে যান
          </HomeButton>

          <OrganicTrustStrip>
            🌿 100% Organic &nbsp;·&nbsp; 🔒 Secure &nbsp;·&nbsp; 🚚 Farm Fresh Delivery
          </OrganicTrustStrip>

        </Container>
      </PageWrapper>
    </>
  );
}

export default ThankYouPage;